// src/features/inventory/pages/CreateItemPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useInventoryItems } from '../hooks/useInventoryItems';
import { useProductGroups } from '../hooks/useProductGroups';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { FormPage, FormSection, FormRow, FormActions } from '../../../components/common/form';

const EMPTY_FORM = {
  product_group_id: '',
  sku: '',
  name: '',
  description: '',
  uom: '',
};

export default function CreateItemPage() {
  const { t } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { showSuccess, showError } = useSnackbar();

  const { loading, error, clearError, createItem } = useInventoryItems();
  const { productGroups, fetchProductGroups } = useProductGroups();

  const [values, setValues] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchProductGroups();
  }, [fetchProductGroups]);

  const handleChange = useCallback((field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  const handleSubmit = async () => {
    const newErrors = {};
    if (!values.product_group_id) newErrors.product_group_id = t('inventory:items.validation.product_group_required');
    if (!values.sku.trim()) newErrors.sku = t('inventory:items.validation.sku_required');
    if (!values.name.trim()) newErrors.name = t('inventory:items.validation.name_required');
    if (!values.uom.trim()) newErrors.uom = t('inventory:items.validation.uom_required');
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const result = await createItem({
      product_group_id: values.product_group_id,
      sku: values.sku.trim(),
      name: values.name.trim(),
      description: values.description.trim() || null,
      uom: values.uom.trim(),
    });

    if (result.success) {
      showSuccess(t('inventory:items.success.created'));
      navigate(`../${result.data.data.item_id}`, { replace: true });
    } else {
      showError(result.error);
    }
  };

  const handleCancel = () => navigateBack('/inventory/items');

  return (
    <FormPage
      title={t('inventory:items.create_form.title')}
      subtitle={t('inventory:items.create_form.subtitle')}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={errors}
      error={error}
      onErrorClose={clearError}
    >
      <FormSection>
        <FormControl fullWidth required error={Boolean(errors.product_group_id)}>
          <InputLabel id="create-item-page-product-group-label">{t('inventory:items.create_form.product_group')}</InputLabel>
          <Select labelId="create-item-page-product-group-label"
            value={values.product_group_id}
            onChange={(e) => handleChange('product_group_id', e.target.value)}
            label={t('inventory:items.create_form.product_group')}
          >
            {productGroups.map((g) => (
              <MenuItem key={g.product_group_id} value={g.product_group_id}>
                {g.code} · {g.name}
              </MenuItem>
            ))}
          </Select>
          {errors.product_group_id && (
            <FormHelperText>{errors.product_group_id}</FormHelperText>
          )}
        </FormControl>

        <TextField
          label={t('inventory:items.create_form.name')}
          value={values.name}
          onChange={(e) => handleChange('name', e.target.value)}
          fullWidth
          required
          error={Boolean(errors.name)}
          helperText={errors.name}
        />

        <FormRow widths={[2, 1]}>
          <TextField
            label={t('inventory:items.create_form.sku')}
            value={values.sku}
            onChange={(e) => handleChange('sku', e.target.value)}
            fullWidth
            required
            error={Boolean(errors.sku)}
            helperText={errors.sku}
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            label={t('inventory:items.create_form.uom')}
            value={values.uom}
            onChange={(e) => handleChange('uom', e.target.value)}
            fullWidth
            required
            error={Boolean(errors.uom)}
            helperText={errors.uom}
            inputProps={{ maxLength: 20 }}
          />
        </FormRow>

        <TextField
          label={t('inventory:items.create_form.description')}
          value={values.description}
          onChange={(e) => handleChange('description', e.target.value)}
          fullWidth
          multiline
          rows={3}
          inputProps={{ maxLength: 1000 }}
        />
      </FormSection>

      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={t('common:buttons.create')}
        submitIcon={<SaveIcon />}
        loading={loading}
      />
    </FormPage>
  );
}
